import { useIntl } from 'react-intl';

import { ActiveStay, PastStay } from 'src/features/stay/types';
import { visitorTypeBadgeOptions } from 'src/features/visitor/utils/constants';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import {
    SidePanelBodyAttribute,
    SidePanelBodyAttributeBody,
    SidePanelBodyAttributeTitle,
} from 'src/registry/components/sidePanel/SidePanelBody';
import { getFormattedDateTimeForDetailPanel } from 'src/utils/getFormattedDateTime';
import { useLiveStayDuration } from 'src/hooks/useLiveStayDuration';
import { commonMessages } from 'src/utils/messages';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';
import { LinkWithIcon } from 'src/registry/components/navigation/LinkWithIcon';

type StayOverviewProps = {
    selectedStay?: ActiveStay | PastStay;
};

const OverviewActiveStays = ({ selectedStay }: StayOverviewProps) => {
    const { formatMessage, formatNumber } = useIntl();

    const accessVisitors = useHasPermissions([PERMISSION.ACCESS_VISITORS]);

    const durationToDisplay = useLiveStayDuration(selectedStay?.entry_time, selectedStay?.exit_time);

    const translatedVisitorType = selectedStay?.visitor.type
        ? `(${formatMessage(visitorTypeBadgeOptions[selectedStay?.visitor.type].label).toLowerCase()})`
        : '';

    const formattedEntryTime = getFormattedDateTimeForDetailPanel(selectedStay?.entry_time);
    const ownerName = selectedStay?.visitor.owner?.name;
    const ownerNameDisplay = ownerName ? `- ${ownerName}` : '';
    const visitorDetails = `${selectedStay?.main_identifier.value} ${translatedVisitorType} ${ownerNameDisplay}`;
    const dueAmount = selectedStay?.total_due_amount ?? selectedStay?.total_amounts?.total_due_amount_with_tax ?? '-';

    const totalPaidAmount = selectedStay?.total_amounts?.total_paid_amount
        ? formatNumber(selectedStay?.total_amounts?.total_paid_amount / 100, {
              style: 'currency',
              currency: selectedStay?.currency,
          })
        : undefined;

    const additionalIdentifiers = selectedStay?.additional_identifiers.map((identifier) => identifier.value).join(', ');

    const getExitTime = () => {
        if (selectedStay && 'exit_time' in selectedStay) {
            return getFormattedDateTimeForDetailPanel(selectedStay.exit_time);
        }
        return '-';
    };

    return (
        <div className="mt-4 grid gap-2">
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>{formatMessage(visitorMessages.visitor)}</SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control={selectedStay?.main_identifier.value} fallbackMessage="-">
                    <LinkWithIcon
                        to={selectedStay?.visitor.id ? `/visitors/visitors/${selectedStay?.visitor.id}` : ''}
                        linkTitle={visitorDetails}
                        hasPermissions={accessVisitors}
                    />
                </SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>
                    {formatMessage(visitorMessages.additionalIdentifiers)}
                </SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody
                    control={selectedStay?.additional_identifiers?.length !== 0}
                    fallbackMessage="-"
                >
                    {additionalIdentifiers ?? '-'}
                </SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>{formatMessage(visitorMessages.paymentDue)}</SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control={selectedStay} fallbackMessage="-">
                    {dueAmount}
                </SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>{formatMessage(visitorMessages.paidAmount)}</SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control={selectedStay} fallbackMessage="-">
                    {totalPaidAmount}
                </SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>{formatMessage(visitorMessages.enteredAt)}</SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control={selectedStay?.entry_time} fallbackMessage="-">
                    {formattedEntryTime}
                </SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>{formatMessage(visitorMessages.exit)}</SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody control={selectedStay} fallbackMessage="-">
                    {getExitTime()}
                </SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
            <SidePanelBodyAttribute>
                <SidePanelBodyAttributeTitle>{formatMessage(visitorMessages.duration)}</SidePanelBodyAttributeTitle>
                <SidePanelBodyAttributeBody
                    control={durationToDisplay}
                    fallbackMessage={formatMessage(commonMessages.minutes, { value: 0 })}
                >
                    {durationToDisplay}
                </SidePanelBodyAttributeBody>
            </SidePanelBodyAttribute>
        </div>
    );
};

export default OverviewActiveStays;
